import React from "react";
import {
  Upload,
  Table,
  Card,
  Statistic,
  Row,
  Col,
  Alert,
  Modal,
  Progress,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { ChantButton, ChantSwitch } from "../../../../../../CommonComponents";

import useMemberListUploader from "./useMemberListUploader";

const { Dragger } = Upload;

const MemberListUploader = ({ open, onCancel }) => {
  const {
    MAX_IMPORT_LIMIT,
    showInvalidMembers,
    isImporting,
    importProgress,
    invalidMembers,
    allMembers,
    uploadStats,
    uploadProps,
    columns,
    isProcessing,
    handleSaveMembers,
    setShowInvalidMembers,
    handleCancel,
    handleModalCancel,
  } = useMemberListUploader(onCancel);

  return (
    <Modal
      open={open}
      onCancel={handleModalCancel}
      width={1000}
      footer={null}
      closable={!isImporting}
      maskClosable={!isImporting}
    >
      <div style={{ margin: "0 auto", padding: 24 }}>
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 28, fontWeight: "bold", marginBottom: 8 }}>
            Member Upload
          </h1>
          <p style={{ color: "#666", fontSize: 16 }}>
            Upload a CSV file with member data (Name, Email columns required).
            Maximum {MAX_IMPORT_LIMIT} records allowed.
          </p>
        </div>

        {uploadStats.total === 0 && (
          <Card style={{ marginBottom: 24 }}>
            <Dragger
              {...uploadProps}
              style={{
                padding: 20,
                opacity: isProcessing || isImporting ? 0.6 : 1,
                pointerEvents: isProcessing || isImporting ? "none" : "auto",
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined
                  style={{
                    fontSize: 48,
                    color: isProcessing || isImporting ? "#ccc" : "#1890ff",
                  }}
                />
              </p>
              <p
                className="ant-upload-text"
                style={{ fontSize: 16, marginBottom: 8 }}
              >
                {isImporting
                  ? "Import in progress - upload disabled"
                  : isProcessing
                    ? "Processing file..."
                    : "Click or drag CSV file to this area to upload"}
              </p>
              <p className="ant-upload-hint" style={{ color: "#666" }}>
                {isImporting
                  ? "Please wait for the current import to complete"
                  : `CSV should contain 'Name' and 'Email' columns. Maximum ${MAX_IMPORT_LIMIT} records allowed. Only single file upload is supported.`}
              </p>
            </Dragger>
          </Card>
        )}

        {isProcessing && (
          <Alert
            message="Processing CSV file..."
            description="Please wait while we validate your data"
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        {isImporting && (
          <Card style={{ marginBottom: 24 }}>
            <Alert
              message="Importing members..."
              description="Please wait while we save your members to the database"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Progress
              percent={importProgress}
              status={importProgress === 100 ? "success" : "active"}
              strokeColor={{
                "0%": "#108ee9",
                "100%": "#87d068",
              }}
              showInfo={true}
              format={(percent) => `${percent}%`}
            />
          </Card>
        )}

        {uploadStats.total > 0 && (
          <Card title="Upload Summary" style={{ marginBottom: 24 }}>
            <Row gutter={16}>
              <Col span={8}>
                <Statistic
                  title="Total Records"
                  value={uploadStats.total}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Valid Records"
                  value={uploadStats.valid}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Invalid Records"
                  value={uploadStats.invalid}
                  valueStyle={{ color: "#ff4d4f" }}
                />
              </Col>
            </Row>
          </Card>
        )}

        {!isImporting && allMembers.length > 0 && (
          <Card
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <p style={{ fontSize: 16, fontWeight: "bold" }}>
                  Member Validation Results
                </p>
                {invalidMembers.length > 0 && (
                  <ChantSwitch
                    label="Show invalid members only"
                    size="small"
                    checked={showInvalidMembers}
                    onChange={() => setShowInvalidMembers(!showInvalidMembers)}
                    disabled={isImporting}
                  />
                )}
              </div>
            }
            style={{
              opacity: isImporting ? 0.7 : 1,
              pointerEvents: isImporting ? "none" : "auto",
            }}
          >
            <div style={{ maxHeight: "30vh", overflowY: "auto" }}>
              <Table
                sticky={{
                  offsetHeader: 0,
                  offsetFooter: 0,
                  getContainer: () => document.body,
                }}
                columns={columns}
                dataSource={showInvalidMembers ? invalidMembers : allMembers}
                pagination={{
                  pageSize: 100,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} records`,
                }}
                scroll={{ x: "max-content" }}
                rowClassName={(record) =>
                  record.isValid
                    ? "ant-table-row-success"
                    : "ant-table-row-error"
                }
              />
            </div>
          </Card>
        )}

        {uploadStats.valid > 0 && (
          <div
            style={{
              marginTop: 16,
              textAlign: "center",
              display: "flex",
              gap: 16,
              justifyContent: "center",
            }}
          >
            <ChantButton type="secondary" onClick={handleCancel}>
              Cancel
            </ChantButton>
            <ChantButton
              type="primary"
              size="large"
              onClick={handleSaveMembers}
              loading={isImporting}
              disabled={isImporting}
            >
              {isImporting
                ? "Importing Members..."
                : `Import ${uploadStats.valid} Valid Members`}
            </ChantButton>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default MemberListUploader;
